import React from 'react';
import PropTypes from 'prop-types';
import { Field, useFetch, useFeatures } from '@cordata/buffet';
import { Row, Col } from 'reactstrap';
import { useFormikContext } from 'formik';
import { camelCase } from 'lodash';

import { deburrObject } from '~/utils/api/normalizers';
import { ReferenceDataField } from '~/components/ReferenceData';

const transformOption = label => ({
  label,
  value: camelCase(label),
});

const Fields = ({ disabled }) => {
  const { hasAppCit } = useFeatures();

  // OBR Creation Rules: For CIT context (hasAppCit), only show facility or division options
  const toFields = hasAppCit
    ? [
      'Facility',
      'Division',
    ].map(transformOption)
    : [
      'Provider',
      'Facility',
      'Division',
    ].map(transformOption);

  const { values } = useFormikContext();
  const [, specialties] = useFetch('provider-roles');
  const [, providers] = useFetch('providers');
  const [, facilities] = useFetch('reference/facilities');
  const [, divisions] = useFetch('divisions');

  // Simple sort to put mental health specialties first in CIT context
  const availableSpecialties = hasAppCit && specialties
    ? [...specialties].sort((a, b) => {
      const aIsMentalHealth = a.attributes?.name?.toLowerCase().includes('mental health');
      const bIsMentalHealth = b.attributes?.name?.toLowerCase().includes('mental health');

      if (aIsMentalHealth && !bIsMentalHealth) return -1;
      if (!aIsMentalHealth && bIsMentalHealth) return 1;
      return 0;
    })
    : specialties;

  return (
    <fieldset disabled={disabled}>
      <Row form>
        <Col sm={5}>
          <Field
            name="specialty"
            type="select"
            options={(availableSpecialties || []).map(deburrObject)}
            getOptionValue={o => o.id}
          />
        </Col>
        <Col sm={4}>
          <Field name="eventDate" type="date" required />
        </Col>

        <Col sm={3}>
          <ReferenceDataField
            name="status"
            label="Referral Status"
            params={{
              ref_data_type: 'outbound referral',
            }}
            required
            disabled={hasAppCit}
          />
        </Col>
        <Col sm={4}>
          <Field
            name="fromProvider"
            type="select"
            getOptionValue={o => o.id}
            getOptionLabel={o => o.displayName}
            options={(providers || []).map(deburrObject)}
            required
          />
        </Col>

        <Col sm={4}>
          <Field
            name="to"
            label="Refer To"
            type="radio-group"
            options={toFields}
            inline
            required
          />
        </Col>

        {values.to === 'provider' && (
          <Col sm={4}>
            <Field
              name="toProvider"
              type="select"
              getOptionValue={o => o.id}
              getOptionLabel={o => o.displayName}
              options={(providers || []).map(deburrObject)}
              required
            />
          </Col>
        )}

        {values.to === 'facility' && (
          <Col sm={4}>
            <Field
              name="toFacility"
              type="select"
              options={(facilities || []).map(deburrObject)}
              getOptionValue={o => o.id}
              getOptionLabel={o => o.name || o.facilityName || o['facility-name']}
              required
            />
          </Col>
        )}

        {values.to === 'division' && (
          <Col sm={4}>
            <Field
              name="toDivision"
              type="select"
              options={(divisions || []).map(deburrObject)}
              getOptionValue={o => o.id}
              getOptionLabel={o => o.name}
              required
            />
          </Col>
        )}

        <Col sm={6}>
          <ReferenceDataField
            name="priority"
            required
          />
        </Col>
        <Col sm={6}>
          <ReferenceDataField
            name="reason"
            params={{
              ref_data_type: 'outbound referral',
            }}
          />
        </Col>
      </Row>
    </fieldset>
  );
};

Fields.propTypes = {
  disabled: PropTypes.bool,
};

Fields.defaultProps = {
  disabled: false,
};

export default Fields;
